import React, { useState, useCallback } from 'react';
import { useTheme, Typography, Stack } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { Company } from '../../../../interfaces/users.interface';
import NewApplication from '../ApplicationModals/NewApplication';
import InhouseApplicationsCards from './InhouseApplications/InhouseApplicationsCards';
import LicensedApplicationsCards from './LicensedApplications/LicensedApplicationsCards';

export const CompanyApplicationsTab = (props: {
  company: Company;
  userCanAdminCompany: boolean;
  companyCallback: any;
  addApplicationOpen?: boolean;
  onAddApplicationClose?: () => void;
}) => {
  const theme = useTheme();
  const { company, companyCallback, userCanAdminCompany, addApplicationOpen: externalAddApplicationOpen, onAddApplicationClose } = props;

  const [internalAddApplicationOpen, setInternalAddApplicationOpen] = useState(false);
  // Bumped when the tab-level "Add Application" modal succeeds, so the
  // self-paginating card sections re-fetch their current page.
  const [reloadToken, setReloadToken] = useState(0);

  const addApplicationOpen = externalAddApplicationOpen !== undefined ? externalAddApplicationOpen : internalAddApplicationOpen;

  const handleAddApplicationClose = useCallback(() => {
    if (onAddApplicationClose) {
      onAddApplicationClose();
    } else {
      setInternalAddApplicationOpen(false);
    }
  }, [onAddApplicationClose]);

  const handleApplicationsChange = useCallback(
    (updatedCompany: Company) => {
      companyCallback(updatedCompany);
      setReloadToken((token) => token + 1);
    },
    [companyCallback],
  );

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      {/* The add modal's site/service selectors fetch their own options
          (searchable, server-paginated), so no full company object is needed. */}
      <NewApplication
        isOpen={addApplicationOpen}
        company={company}
        callBack={(co: Company) => {
          handleApplicationsChange(co);
          handleAddApplicationClose();
        }}
        handleClose={handleAddApplicationClose}
      />

      <Typography
        variant="body1"
        sx={{
          backgroundColor: '#F3F5FB',
          color: theme.palette.text.primary,
          fontSize: '0.95rem',
          padding: 2,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <InfoOutlined fontSize="inherit" />
        Click the Application Version row to see more details in the sidebar
      </Typography>

      <InhouseApplicationsCards
        company={company}
        userCanAdminCompany={userCanAdminCompany}
        companyCallback={handleApplicationsChange}
        reloadToken={reloadToken}
      />

      <LicensedApplicationsCards
        company={company}
        userCanAdminCompany={userCanAdminCompany}
        companyCallback={handleApplicationsChange}
        reloadToken={reloadToken}
      />
    </Stack>
  );
};

export default CompanyApplicationsTab;
